import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DRINKS, INGREDIENTS } from '@/lib/gameData';
import { useCafe3dStore } from '@/stores/cafe3dStore';

/**
 * Hash order_id stable thành index → mỗi order luôn show 1 dialogue cố định
 * (không nhảy mỗi render). Cùng order_id cho cùng output → no flicker.
 */
function dialogueIndexForOrder(orderId: string | undefined, total: number): number {
  if (!orderId || total <= 0) return 0;
  let h = 0;
  for (let i = 0; i < orderId.length; i++) {
    h = (h * 31 + orderId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % total;
}

export function DialogueBubble() {
  const { t, i18n } = useTranslation();
  const customer = useCafe3dStore((s) => s.currentCustomer);
  const sceneState = useCafe3dStore((s) => s.sceneState);
  const orderSpawnedAt = useCafe3dStore((s) => s.orderSpawnedAt);
  const [, forceUpdate] = useState(0);

  // Tick for patience bar
  useEffect(() => {
    if (sceneState !== 'ordering') return;
    const id = setInterval(() => forceUpdate((n) => n + 1), 200);
    return () => clearInterval(id);
  }, [sceneState]);

  // Auto timeout
  useEffect(() => {
    if (sceneState !== 'ordering' || !customer) return;
    const elapsed = (Date.now() - orderSpawnedAt) / 1000;
    if (elapsed > customer.patience_seconds) {
      useCafe3dStore.getState().customerTimeout();
    }
  });

  if (!customer || sceneState === 'idle' || sceneState === 'customer_walking_in') {
    return null;
  }

  // Tên món GIỮ tiếng Việt trong mọi locale (bản sắc cà phê Việt).
  const drink = DRINKS[customer.drink];

  // Dialogue: lookup từ i18n theo customer.type + index hash từ order_id.
  // Backend trả `dialogue_vi` cho mọi order nhưng chỉ tiếng Việt;
  // ở các locale khác → FE generate dialogue locally để khách "nói" đúng ngôn ngữ user.
  // Fallback: nếu i18n không có (vd thiếu key) → dùng dialogue_vi từ backend.
  const dialoguesArray = t(`dialogues.${customer.type}`, {
    returnObjects: true,
    defaultValue: [],
  }) as string[];
  const dialogueIdx = dialogueIndexForOrder(customer.id, dialoguesArray.length);
  const dialogue =
    dialoguesArray.length > 0
      ? dialoguesArray[dialogueIdx]
      : customer.dialogue_vi;

  // Recipe translation — VN keeps original, non-vi shows bản dịch dưới tên VN.
  const recipeTranslation = t(`recipe_translation.${customer.drink}`, '');
  const showTranslation =
    i18n.language !== 'vi' && recipeTranslation && recipeTranslation.length > 0;

  const elapsed = (Date.now() - orderSpawnedAt) / 1000;
  const patienceRatio = Math.max(0, 1 - elapsed / customer.patience_seconds);

  return (
    <div className="absolute top-[120px] left-1/2 -translate-x-1/2 w-[88%] max-w-[420px] z-20 pointer-events-none">
      {/* Speech bubble */}
      <div className="bg-cream-50 border border-coffee-700/15 rounded-2xl px-4 py-3 shadow-lg animate-pop-in relative">
        <p className="text-base font-medium text-coffee-900">{dialogue}</p>
        {/* Tail */}
        <div className="absolute -bottom-2 left-8 w-4 h-4 bg-cream-50 border-r border-b border-coffee-700/15 transform rotate-45" />
      </div>

      {/* Order card */}
      <div className="mt-2 bg-coffee-900/95 text-cream-50 rounded-xl px-3 py-2 flex items-center gap-3">
        <div className="text-2xl">{drink.emoji}</div>
        <div className="flex-1">
          <div className="text-sm font-medium">{drink.name_vi}</div>
          {showTranslation && (
            <div className="text-[10px] italic text-cream-50/60 leading-tight">
              {recipeTranslation}
            </div>
          )}
          <div className="text-xs opacity-70 flex gap-1 mt-0.5">
            {drink.recipe.map((ing: string, i: number) => (
              <span key={i} title={t(`ingredient.${ing}`)}>
                {INGREDIENTS[ing].emoji}
                {i < drink.recipe.length - 1 && ' +'}
              </span>
            ))}
          </div>
        </div>
        {/* Patience indicator */}
        <PatienceRing ratio={patienceRatio} />
      </div>
    </div>
  );
}

function PatienceRing({ ratio }: { ratio: number }) {
  const radius = 14;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - ratio);
  const color = ratio > 0.5 ? '#5C8A4F' : ratio > 0.2 ? '#F2C94C' : '#C73E3A';

  return (
    <svg width="36" height="36" className="-rotate-90">
      <circle cx="18" cy="18" r={radius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.3s linear, stroke 0.3s' }}
      />
    </svg>
  );
}
